import { BaseAttribute } from '../../../../types/attributes';
import { getAttrFromProductByName } from '../../../../types/attrNames';
import { CmsProduct } from '../../../../types/cmsProduct';
import {
  formatAttributeItemValue,
  formatNumberWithSeparator,
} from '../../../../utils/helpers';
import { PdpAttributeRowConfig } from '../../Sections/Configured/types';

/**
 * A configured row resolved against the active product, ready for a dumb renderer.
 * - `value`: one "label — value" line (numbers, ranges, plain text, booleans);
 * - `list`: a titled bullet list (key-value attributes such as QUICK_FACTS, payment networks,
 *   multi-text). Its items are trimmed, non-empty and each one is there once: a value entered twice
 *   (an authoring slip) shows once on every surface that renders the row;
 * - `richText`: a titled rich-text block (e.g. STEPS_TO_APPLY, PRODUCT_HIGHLIGHT_SECTION).
 * `attributeKey` is kept so renderers can special-case a known attribute (e.g. the Quick Facts icon).
 */
export type ResolvedRow = { attributeKey: string; label: string } & (
  | {
      kind: 'value';
      value: string;
      additionalInformation?: string;
    }
  | { kind: 'list'; items: string[] }
  | { kind: 'richText'; html: string }
);

/** A configured group with its rows resolved against the product. */
export type ResolvedGroup = {
  id: string;
  title?: string;
  collapsible: boolean;
  rows: ResolvedRow[];
};

const nonEmpty = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Number / number-range display value, formatted the way the default attribute list does it: prefix
 * and suffix from `formatAttributeItemValue`, then the thousands separator over the whole string so a
 * range gets it on both ends too.
 */
const formatNumberLike = (attr: BaseAttribute): string =>
  formatNumberWithSeparator({
    value: formatAttributeItemValue(attr, true),
    thousandsSeparator: ',',
  }).trim();

/** The items of a `list` row: trimmed, non-empty, each value once (see `ResolvedRow`). */
const toListItems = (values: Array<string | undefined>): string[] => [
  ...new Set(values.map((value) => value?.trim() ?? '').filter(Boolean)),
];

/** The values of a selection / multi-text attribute as `list` items. */
const toStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? toListItems(
        value.map((item) =>
          typeof item === 'string'
            ? item
            : typeof item?.value === 'string'
              ? item.value
              : undefined,
        ),
      )
    : [];

/**
 * Resolves one configured row (attribute NAME + optional label override) against the product.
 * Returns `undefined` when the product has no attribute of that name, the attribute has no usable
 * value, or its type is not renderable as a detail row (tables, JSON, add-ons) — the row then
 * auto-hides, no "—" placeholder (same contract as the TI eligibility rows, CMS-1644 §6.3).
 *
 * Label precedence: editor override → the attribute's own label on the product → the catalogue label
 * mapped by the fe → the catalogue label the product's attribute carries (`name.label`, for callers
 * that pass no row label) → the attribute NAME.
 */
export function resolveAttributeRow(
  product: CmsProduct | undefined,
  row: PdpAttributeRowConfig,
): ResolvedRow | undefined {
  const attr = getAttrFromProductByName(row.attributeKey, product);
  if (!attr) return undefined;

  const { attributeKey } = row;
  const label =
    row.displayLabelOverride?.trim() ||
    attr.label?.trim() ||
    row.label?.trim() ||
    attr.name?.label?.trim() ||
    row.attributeKey;

  switch (attr.__typename) {
    case 'ComponentAttributesAttrNumber': {
      // `formatAttributeItemValue` renders `empty_value_text` (e.g. "Free") for a value-less number;
      // only a number with neither a value nor that text hides.
      if (!nonEmpty(attr.value) && !nonEmpty(attr.empty_value_text)) {
        return undefined;
      }
      const value = formatNumberLike(attr);
      return value
        ? {
            attributeKey,
            kind: 'value',
            label,
            value,
            additionalInformation: attr.additional_information || undefined,
          }
        : undefined;
    }
    case 'ComponentAttributesAttrNumberRange': {
      if (!nonEmpty(attr.value_start)) return undefined;
      const value = formatNumberLike(attr);
      return value
        ? {
            attributeKey,
            kind: 'value',
            label,
            value,
            additionalInformation: attr.additional_information || undefined,
          }
        : undefined;
    }
    case 'ComponentAttributesAttrPlainText': {
      const value = attr.value?.trim();
      return value ? { attributeKey, kind: 'value', label, value } : undefined;
    }
    case 'ComponentAttributesAttrBoolean': {
      if (typeof attr.value !== 'boolean') return undefined;
      const value = (attr.value ? attr.true_value : attr.false_value)?.trim();
      return value ? { attributeKey, kind: 'value', label, value } : undefined;
    }
    case 'ComponentAttributesAttrPaymentNetwork':
    case 'ComponentAttributesAttrPaymentType':
    case 'ComponentAttributesMultiText': {
      const items = toStrings(attr.value);
      return items.length > 0
        ? { attributeKey, kind: 'list', label, items }
        : undefined;
    }
    case 'ComponentAttributesAttrKeyValue': {
      const items = toListItems(
        (attr.value ?? [])
          .flatMap((entry) => entry?.values ?? [])
          .map((entry) => entry?.value),
      );
      return items.length > 0
        ? { attributeKey, kind: 'list', label, items }
        : undefined;
    }
    case 'ComponentAttributesAttrRichTextEditor': {
      const html = typeof attr.value === 'string' ? attr.value.trim() : '';
      return html ? { attributeKey, kind: 'richText', label, html } : undefined;
    }
    default:
      // Tables, rate tables, JSON and add-ons have no detail-row rendering yet (the two tables in
      // the CMS-1935 scope have no catalogue NAME either) — skip rather than render garbage.
      return undefined;
  }
}
